import type {
  StoryCollaboratorRow,
  StoryMember,
  StoryMemberRole,
  StoryMemberApi,
  StoryMembers,
  StoryMembersResponseApi,
} from "./storyMembers.types";
import { toAvatarUrl } from "../account/accountProfileMappers";

/**
 * Display name precedence: full_name → email local-part → email.
 * The email local-part keeps the initials fallback meaningful for users who
 * were invited but never completed their profile ("d" beats "?").
 */
export const storyMemberDisplayName = (
  api: Pick<StoryMemberApi, "full_name" | "email">,
): string => {
  const fullName = api.full_name?.trim();
  if (fullName) return fullName;
  return api.email.split("@")[0]?.trim() || api.email;
};

export const mapStoryMember = (api: StoryMemberApi): StoryMember => ({
  id: api.user_id,
  name: storyMemberDisplayName(api),
  email: api.email,
  role: api.role,
  // Empty strings are treated as "no picture" so the avatar never renders a
  // broken <img> — AvatarStack branches on falsy avatarUrl.
  avatarUrl: toAvatarUrl(api.profile_picture),
  joinedAt: api.joined_at,
});

export const mapStoryMembersResponse = (
  api: StoryMembersResponseApi,
): StoryMembers => ({
  storyId: api.story_id,
  items: (api.items ?? []).map(mapStoryMember),
  total: api.total ?? api.items?.length ?? 0,
});

export const STORY_MEMBER_ROLE_LABELS: Record<StoryMemberRole, string> = {
  owner: "Owner",
  editor: "Editor",
  viewer: "Viewer",
};

/** Owner first, then editors, then viewers; original order kept within a role. */
const ROLE_ORDER: StoryMemberRole[] = ["owner", "editor", "viewer"];

export const mapStoryMembersToCollaboratorRows = (
  members: StoryMember[],
): StoryCollaboratorRow[] =>
  [...members]
    .sort((a, b) => ROLE_ORDER.indexOf(a.role) - ROLE_ORDER.indexOf(b.role))
    .map((member) => ({
      id: member.id,
      name: member.name,
      email: member.email,
      roleLabel: STORY_MEMBER_ROLE_LABELS[member.role],
      avatarUrl: member.avatarUrl,
    }));
